import React, { type ReactNode } from 'react'
import { View } from 'react-native'
import { twMerge } from 'tailwind-merge'

import { Typography } from '../../typography/Typography'
import { variantForStyleId } from '../../typography/variants'
import { tooltipContract } from './tooltip.contract'

const VARIANTS = tooltipContract.props.variant.values

export type TooltipVariant = (typeof VARIANTS)[number]

export type TooltipProps = {
    children: ReactNode
    variant?: TooltipVariant
    className?: string
    testID?: string
}

export const TOOLTIP_TEXT = variantForStyleId(tooltipContract.textStyleIds[0])

/**
 * The surface a tooltip's content sits on.
 *
 * This renders the shell and nothing else, and that is a decision rather than an
 * omission. The set has four variants and not one of them names a part after a
 * role:
 *
 * | variant       | own parts | what they are named                                  |
 * | ------------- | --------- | ---------------------------------------------------- |
 * | `Simple`      | 1         | `Accept` — the label's own text                      |
 * | `Complex`     | 25        | `8`, `28`, `32`, `02:15`, `Temperature`, `°C`        |
 * | `four-ranges` | 14        | `Thunderstorm probability`, `Unlikely, yet possible` |
 * | `two-ranges`  | 8         | `Likely`, `Highly probable`                          |
 *
 * Those part names are the sample content of a weather tooltip, not roles — and
 * the contract's own rule says a key that reads as a value rather than a role
 * means a composition, not a primitive. Generating components from them would
 * put `Thunderstorm probability` and `02:15` inside the design system, where the
 * weather feature could not change them and design could not either.
 *
 * What all four genuinely share is the shell: `background/elevation`, a `common`
 * radius, `small-compact/s` padding, and a gap that is the only thing the variant
 * actually selects — `Simple` has none, `Complex` uses `xs`, both range variants
 * use `s`. So `variant` picks the spacing, `children` is the caller's content,
 * and the row inks stay where design can reach them.
 *
 * Behaviour — trigger, placement, dismissal — is gluestack's and does not belong
 * here, the same split `Spinner` makes for motion.
 */
const variantFor = (variant: TooltipVariant) => {
    const key = `variant=${variant}`
    const found = (
        tooltipContract.variants as Record<
            string,
            { container: string; label: string; icon: string }
        >
    )[key]
    if (!found) {
        throw new Error(`aos-ds Tooltip: the Figma contract has no "${key}"`)
    }
    return found
}

/**
 * The ink for a row's leading dot, per variant — `graphics/teal` in `Complex`,
 * `graphics/pink` in both range variants. Exposed rather than drawn because the
 * dot belongs to a row the caller builds, and reading it here is what keeps that
 * row's colour a design decision.
 */
export const tooltipInk = (variant: TooltipVariant): string => variantFor(variant).icon

export const Tooltip = ({ children, variant = 'Simple', className, testID }: TooltipProps) => {
    const { container, label } = variantFor(variant)
    return (
        <View
            className={twMerge('self-start', container, className)}
            testID={testID ?? `tooltip-${variant}`}
        >
            {typeof children === 'string' ? (
                <Typography variant={TOOLTIP_TEXT} className={label} testID='tooltip-label'>
                    {children}
                </Typography>
            ) : (
                children
            )}
        </View>
    )
}

Tooltip.displayName = 'Tooltip'

export { VARIANTS as TOOLTIP_VARIANTS }
